import { useEffect, useRef, memo } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
}

interface ParticleNetworkProps {
  className?: string;
}

export const ParticleNetwork = memo(function ParticleNetwork({ className }: ParticleNetworkProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const particlesRef = useRef<Particle[]>([] as Particle[]);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Prevent re-initialization if already initialized
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Configuration
    const particleCount = 80;
    const connectionDistance = 150;
    const maxConnections = 3;
    const particleSpeed = 0.5;
    const particleRadius = 2;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize particles
    const initParticles = () => {
      particlesRef.current = [];
      for (let i = 0; i < particleCount; i++) {
        particlesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * particleSpeed,
          vy: (Math.random() - 0.5) * particleSpeed,
          radius: particleRadius,
        });
      }
    };

    initParticles();

    // Get CSS variable color
    const getPrimaryColor = () => {
      const root = document.documentElement;
      const styles = getComputedStyle(root);
      // Try to get the primary color from CSS variables
      const primary = styles.getPropertyValue('--color-primary').trim();
      if (primary) return primary;
      return 'hsl(193 97% 52%)'; // Fallback to cyan
    };

    // Animation loop
    const animate = () => {
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const primaryColor = getPrimaryColor();

      // Update and draw particles
      particlesRef.current.forEach((particle, i) => {
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        // Keep particles in bounds
        particle.x = Math.max(0, Math.min(canvas.width, particle.x));
        particle.y = Math.max(0, Math.min(canvas.height, particle.y));

        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = primaryColor;
        ctx.fill();

        // Draw connections
        let connections = 0;
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          if (connections >= maxConnections) break;

          const other = particlesRef.current[j];
          const dx = particle.x - other.x;
          const dy = particle.y - other.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.5;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = primaryColor.replace(')', ` / ${opacity})`).replace('hsl(', 'hsla(');
            ctx.lineWidth = 1;
            ctx.stroke();
            connections++;
          }
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className || "fixed inset-0 pointer-events-none z-0"}
      style={{ opacity: 0.6 }}
    />
  );
});

export default ParticleNetwork;
